import { FC } from 'react';
import { graphql, useFragment, usePaginationFragment } from 'react-relay';

import { flattenConnectionNodes } from '../../../utils/relayUtils';
import { youMayAlsoLike } from '../../../shared/sharedMessages';
import { PdpSharedProductTileCarousel } from '../../shared/PdpSharedProductTileCarousel/PdpSharedProductTileCarousel';
import { useServerVarsContext } from '../../../global/ServerVarsContext/ServerVarsContext';

import { PdpSharedYMALItemsCarousel_viewer$key } from './__generated__/PdpSharedYMALItemsCarousel_viewer.graphql';
import { PdpSharedYMALItemsCarousel_user$key } from './__generated__/PdpSharedYMALItemsCarousel_user.graphql';
import { PdpSharedYMALItemsCarousel_item$key } from './__generated__/PdpSharedYMALItemsCarousel_item.graphql';

const viewerFragment = graphql`
    fragment PdpSharedYMALItemsCarousel_viewer on Viewer
    @refetchable(queryName: "PdpSharedYMALItemsCarouselPaginationQuery")
    @argumentDefinitions(count: { type: "Int", defaultValue: 6 }, cursor: { type: "String" }) {
        ymalItems: moreLikeThisItem(
            first: $count
            after: $cursor
            seller: EXCLUDE
            itemId: $itemId
            excludeAuctionItems: true
        ) @connection(key: "PdpSharedYMALItemsCarousel_ymalItems", filters: []) {
            edges {
                node {
                    ...PdpSharedProductTileCarousel_items
                        @arguments(
                            userId: $userId
                            isTrade: $isTrade
                            priceBookName: $priceBookName
                        )
                }
            }
            totalResults
        }
        ...PdpSharedProductTileCarousel_viewer @arguments(userId: $userId)
    }
`;

const itemFragment = graphql`
    fragment PdpSharedYMALItemsCarousel_item on Item {
        serviceId
    }
`;

const userFragment = graphql`
    fragment PdpSharedYMALItemsCarousel_user on User {
        ...PdpSharedProductTileCarousel_user
    }
`;

export const PdpSharedYMALItemsCarousel: FC<{
    viewer: PdpSharedYMALItemsCarousel_viewer$key;
    item: PdpSharedYMALItemsCarousel_item$key;
    user?: PdpSharedYMALItemsCarousel_user$key | null;
    currency: string;
}> = ({ viewer: viewerRef, user: userRef = null, item: itemRef, currency }) => {
    const { data: viewer, loadNext } = usePaginationFragment(viewerFragment, viewerRef);
    const user = useFragment(userFragment, userRef);
    const item = useFragment(itemFragment, itemRef);
    const items = flattenConnectionNodes(viewer?.ymalItems);

    const { isSoldPDPV1Variant } = useServerVarsContext();

    const totalResults = viewer.ymalItems?.totalResults || 0;

    if (!items.length) {
        return null;
    }

    return (
        <PdpSharedProductTileCarousel
            viewer={viewer}
            items={items}
            user={user}
            title={youMayAlsoLike}
            viewAllLink={isSoldPDPV1Variant ? `/similar/?item=${item.serviceId}` : undefined}
            currency={currency}
            trackingList="pdp you may also like"
            dataTn="pdp-similar-recommendation-grid"
            totalResults={totalResults}
            carouselCapacity={10}
            loadNext={loadNext}
        />
    );
};
